import type { Money, SupportReport } from '@nucleus-commerce/api-client/admin';
import { toMajorString } from '@nucleus-commerce/core';

type Cell = string | number | null;

/** RFC 4180: quote a field that holds a comma, quote or line break; double inner quotes. */
function cell(value: Cell): string {
  if (value === null) return '';
  const text = String(value);
  return /[",\r\n]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
}

function toCsv(rows: ReadonlyArray<readonly Cell[]>): string {
  return `${rows.map((row) => row.map(cell).join(',')).join('\r\n')}\r\n`;
}

const major = (money: Money) => toMajorString(money);

export type SupportCsvKind = 'daily' | 'agents' | 'tags';

/**
 * The support report as CSV, built from what the page already loaded. Money is in major units
 * of the report currency, durations in seconds, so a spreadsheet can sum and average them.
 */
export function supportCsv(report: SupportReport, kind: SupportCsvKind): string {
  if (kind === 'agents') {
    return toCsv([
      [
        'agent',
        'handled',
        'first_response_seconds_avg',
        'ratings_positive',
        'ratings_neutral',
        'ratings_negative',
        'attributed_orders',
        `attributed_net_${report.currency}`,
      ],
      ...report.agents.map((a) => [
        a.name,
        a.handled,
        a.firstResponseSecondsAvg,
        a.ratingsPositive,
        a.ratingsNeutral,
        a.ratingsNegative,
        a.attributedOrders,
        major(a.attributedNet),
      ]),
    ]);
  }
  if (kind === 'tags') {
    return toCsv([['tag', 'conversations'], ...report.tags.map((t) => [t.tag, t.count])]);
  }
  return toCsv([
    [
      'date',
      'chats_started',
      'chats_missed',
      'offline_messages',
      'tickets_opened',
      'tickets_closed',
      'first_responses',
      'first_response_seconds_avg',
      'first_response_seconds_median',
      'resolved',
      'resolution_seconds_avg',
      'ratings_positive',
      'ratings_neutral',
      'ratings_negative',
      'help_deflections',
      'attributed_orders',
      `attributed_revenue_${report.currency}`,
    ],
    ...report.series.map((d) => [
      d.date,
      d.chatsStarted,
      d.chatsMissed,
      d.offlineMessages,
      d.ticketsOpened,
      d.ticketsClosed,
      d.firstResponses,
      d.firstResponseSecondsAvg,
      d.firstResponseSecondsMedian,
      d.resolved,
      d.resolutionSecondsAvg,
      d.ratingsPositive,
      d.ratingsNeutral,
      d.ratingsNegative,
      d.helpDeflections,
      d.attributedOrders,
      major(d.attributedRevenue),
    ]),
  ]);
}

export function supportCsvFilename(
  report: Pick<SupportReport, 'from' | 'to'>,
  kind: SupportCsvKind,
): string {
  return `support-${kind}-${report.from}-to-${report.to}.csv`;
}
